import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { createClient } from '../../../utils/clientService';

const AddClientForm = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    lifecycle_stage: 'Past Client',
    engagement_level: 'Medium',
    notes: '',
    tags: [],
    birthday: '',
    home_closing_date: ''
  });
  const [tagInput, setTagInput] = useState('');
  const [showMore, setShowMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddTag = () => {
    if (tagInput?.trim() && !formData?.tags?.includes(tagInput?.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev?.tags, tagInput?.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev?.tags?.filter((tag) => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!formData?.name?.trim()) {
      setError('Client name is required');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const payload = {
        ...formData,
        birthday: formData.birthday || null,
        home_closing_date: formData.home_closing_date || null,
      };
      const { data, error } = await createClient(payload);

      if (error) {
        setError(error?.message || 'Failed to create client');
      } else {
        onSuccess?.(data);
        onClose?.();
        setFormData({
          name: '',
          email: '',
          phone: '',
          location: '',
          lifecycle_stage: 'Past Client',
          engagement_level: 'Medium',
          notes: '',
          tags: [],
          birthday: '',
          home_closing_date: ''
        });
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Add New Client</h2>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
              <p className="text-destructive text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <Input
              label="Full Name"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              placeholder="Enter client name"
              required
              disabled={isLoading}
            />
            <Input
              type="tel"
              label="Phone Number"
              value={formData?.phone}
              onChange={(e) => handleInputChange('phone', e?.target?.value)}
              placeholder="e.g. +1 587-329-8707"
              disabled={isLoading}
            />
            <Input
              type="email"
              label="Email (optional)"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              placeholder="Optional"
              disabled={isLoading}
            />
            <Input
              label="Address (optional)"
              value={formData?.location}
              onChange={(e) => handleInputChange('location', e?.target?.value)}
              placeholder="123 Main St, City, ST 12345"
              disabled={isLoading}
            />
          </div>

          <button
            type="button"
            onClick={() => setShowMore((v) => !v)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <Icon name={showMore ? 'ChevronDown' : 'ChevronRight'} size={16} />
            {showMore ? 'Hide optional details' : 'Add optional details'}
          </button>

          {showMore && (
            <div className="space-y-4 pt-2 border-t border-border">
              <Input
                type="date"
                label="Birthday"
                value={formData?.birthday || ''}
                onChange={(e) => handleInputChange('birthday', e?.target?.value)}
                disabled={isLoading}
              />
              <Input
                type="date"
                label="Home closing date"
                value={formData?.home_closing_date || ''}
                onChange={(e) => handleInputChange('home_closing_date', e?.target?.value)}
                disabled={isLoading}
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Notes</label>
                <textarea
                  value={formData?.notes}
                  onChange={(e) => handleInputChange('notes', e?.target?.value)}
                  placeholder="Any notes..."
                  rows={2}
                  disabled={isLoading}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Tags</label>
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e?.target?.value)}
                    placeholder="Add a tag"
                    disabled={isLoading}
                    onKeyPress={(e) => {
                      if (e?.key === 'Enter') {
                        e?.preventDefault();
                        handleAddTag();
                      }
                    }}
                  />
                  <Button type="button" variant="outline" onClick={handleAddTag} disabled={!tagInput?.trim() || isLoading}>
                    Add
                  </Button>
                </div>
                {formData?.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData?.tags?.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary/10 text-primary border border-primary/20"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          disabled={isLoading}
                          className="ml-2 text-primary/70 hover:text-primary disabled:opacity-50"
                        >
                          <Icon name="X" size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Icon name="Loader2" size={16} className="animate-spin mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <Icon name="Plus" size={16} className="mr-2" />
                  Create Client
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddClientForm;